import type { CSSProperties } from 'react';
import { Link, useSearch, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { DuctRun, toDuctStages, STAGE_COLOR, type StageRow } from '@monrdv/ui';
import { formatMoney, toCents, formatPhone, localized } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';

/**
 * The landing screen.
 *
 * Answers the three questions a coordinator opens the day with, in the order they matter:
 * what is happening today, what needs chasing, and what came in overnight.
 *
 * Deliberately not a wall of charts. The old system's dashboard was a grid of counters
 * nobody acted on; every block here is a list of specific things with a name attached,
 * because work gets done against records, not against numbers.
 */

type JobRow = {
  id: string;
  number: string;
  title: string | null;
  stageId: string;
  customer: { id: string; name: string };
  address: { line1: string; city: string; postalCode: string | null; note: string | null } | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  assignees: Array<{ id: string; name: string; initials: string; isLead: boolean }>;
  services: string[];
  rollup: { balance: string; invoicedTotal: string; laborMinutes: number; messageCount: number };
};

type InvoiceRow = {
  id: string;
  number: string;
  status: string;
  total: string;
  balance: string;
  dueOn: string | null;
  jobNumber: string | null;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export function TodayScreen() {
  const { date, assignee } = useSearch({ from: '/' });
  const navigate = useNavigate();
  const day = date ? new Date(date) : new Date();

  const stages = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: () => request<{ stages: StageRow[] }>('/pipeline-stages'),
    staleTime: 10 * 60_000,
  });

  const jobs = useQuery({
    queryKey: ['today', 'jobs', day.toDateString(), assignee ?? null],
    queryFn: () => request<{ items: JobRow[]; total: number }>(`/jobs?limit=200`),
  });

  const invoices = useQuery({
    queryKey: ['receivables'],
    queryFn: () => request<{ items: InvoiceRow[]; outstanding: string; total: number }>('/invoices?limit=200'),
  });

  const ductStages = stages.data ? toDuctStages(stages.data.stages, 'fr') : [];
  const stageById = new Map((stages.data?.stages ?? []).map((s) => [s.id, s]));

  // Today's scheduled work, in the order it happens.
  const from = startOfDay(day);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);

  const scheduled = (jobs.data?.items ?? [])
    .filter((j) => {
      if (!j.scheduledStart) return false;
      const at = new Date(j.scheduledStart);
      return at >= from && at < to;
    })
    .sort((a, b) => (a.scheduledStart ?? '').localeCompare(b.scheduledStart ?? ''));

  // Work with no date at all. Hydrokleen runs a large unscheduled backlog by design, so this
  // is a queue to work through, not an error state.
  const unscheduled = (jobs.data?.items ?? []).filter((j) => {
    const stage = stageById.get(j.stageId);
    return !j.scheduledStart && stage && !['won', 'lost', 'cancelled'].includes(stage.kind);
  });

  const overdue = (invoices.data?.items ?? [])
    .filter((i) => toCents(i.balance) > 0 && i.dueOn && new Date(i.dueOn) < new Date())
    .sort((a, b) => (a.dueOn ?? '').localeCompare(b.dueOn ?? ''));

  const outstandingCents = (invoices.data?.items ?? []).reduce((sum, i) => sum + toCents(i.balance), 0);
  const overdueCents = overdue.reduce((sum, i) => sum + toCents(i.balance), 0);

  const isToday = startOfDay(new Date()).getTime() === from.getTime();

  return (
    <div style={S.page}>
      <header style={S.head}>
        <div>
          <h1 style={S.title}>
            {isToday
              ? "Aujourd'hui"
              : day.toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h1>
          <p style={S.subtitle}>
            {day.toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div style={S.dayNav}>
          <button type="button" style={S.dayBtn} onClick={() => shiftDay(-1)} aria-label="Jour précédent">
            ←
          </button>
          {!isToday && (
            <button type="button" style={S.dayBtn} onClick={() => navigate({ to: '.', search: (p: any) => ({ ...p, date: undefined }) })}>
              Aujourd'hui
            </button>
          )}
          <button type="button" style={S.dayBtn} onClick={() => shiftDay(1)} aria-label="Jour suivant">
            →
          </button>
        </div>
      </header>

      {/* Money first, because unpaid work is the thing that quietly gets forgotten. */}
      <section style={S.moneyRow} aria-label="Sommes à recevoir">
        <MoneyTile label="À recevoir" cents={outstandingCents} tone="neutral" />
        <MoneyTile label="En retard" cents={overdueCents} tone={overdueCents > 0 ? 'caution' : 'neutral'} count={overdue.length} />
        <MoneyTile label="Planifié aujourd'hui" count={scheduled.length} tone="neutral" />
        <MoneyTile label="À planifier" count={unscheduled.length} tone={unscheduled.length > 0 ? 'accent' : 'neutral'} />
      </section>

      <div style={S.columns}>
        <section style={S.panel} aria-label="Travaux planifiés">
          <header style={S.panelHead}>
            <h2 style={S.panelTitle}>Horaire</h2>
            <Link to="/jobs" search={{ view: 'board' } as never} style={S.panelLink}>
              Tableau des travaux
            </Link>
          </header>

          {jobs.isLoading && <p style={S.muted}>Chargement…</p>}
          {!jobs.isLoading && scheduled.length === 0 && (
            <p style={S.muted}>Aucun travail planifié pour cette journée.</p>
          )}

          <ol style={S.list}>
            {scheduled.map((job) => {
              const stage = stageById.get(job.stageId);
              return (
                <li key={job.id} style={S.item}>
                  <span className="tnum" style={S.itemTime}>
                    {job.scheduledStart
                      ? new Date(job.scheduledStart).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </span>
                  <span style={S.itemBody}>
                    <Link to="/jobs/$jobId" params={{ jobId: job.id }} style={S.itemLink}>
                      {job.customer.name}
                    </Link>
                    <span style={S.itemMeta}>
                      {[job.address ? `${job.address.line1}, ${job.address.city}` : null, job.services[0]]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  {stage && (
                    <span style={{ ...S.chip, color: STAGE_COLOR[stage.kind], borderColor: STAGE_COLOR[stage.kind] }}>
                      {localized(stage.name, 'fr')}
                    </span>
                  )}
                  {job.assignees.length === 0 && <span style={S.unassigned}>à assigner</span>}
                </li>
              );
            })}
          </ol>
        </section>

        <section style={S.panel} aria-label="Factures en retard">
          <header style={S.panelHead}>
            <h2 style={S.panelTitle}>En retard</h2>
            <Link to="/invoices" search={{ status: 'awaiting_payment', overdueOnly: true } as never} style={S.panelLink}>
              Toutes les factures
            </Link>
          </header>

          {overdue.length === 0 && <p style={S.muted}>Rien en retard. </p>}

          <ol style={S.list}>
            {overdue.slice(0, 12).map((invoice) => {
              const days = invoice.dueOn
                ? Math.floor((Date.now() - new Date(invoice.dueOn).getTime()) / 86_400_000)
                : 0;
              return (
                <li key={invoice.id} style={S.item}>
                  <span style={S.itemBody}>
                    {/* Was a span styled to look like a link: it read as clickable and was
                        not. RecordLink also honours the reader's open-mode preference. */}
                    <RecordLink kind="invoice" id={invoice.id} style={S.itemLink}>
                      Facture {invoice.number}
                    </RecordLink>
                    <span style={S.itemMeta}>
                      {days} {days === 1 ? 'jour' : 'jours'} de retard
                      {invoice.jobNumber ? ` · travail ${invoice.jobNumber}` : ''}
                    </span>
                  </span>
                  <span className="money" style={S.amountDue}>
                    {formatMoney(toCents(invoice.balance), 'fr')}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>

      {/* The pipeline at a glance, using the same duct run the job rows use. */}
      {ductStages.length > 0 && (
        <section style={S.panel} aria-label="Étapes du pipeline">
          <header style={S.panelHead}>
            <h2 style={S.panelTitle}>Pipeline</h2>
          </header>
          <div style={{ padding: 'var(--space-4) var(--space-2) var(--space-2)' }}>
            <DuctRun
              stages={ductStages}
              currentStageId={ductStages[Math.min(2, ductStages.length - 1)]?.id ?? ''}
              variant="detail"
              label="Étapes du pipeline de cette entreprise"
            />
          </div>
        </section>
      )}
    </div>
  );

  function shiftDay(delta: number) {
    const next = new Date(from);
    next.setDate(next.getDate() + delta);
    void navigate({ to: '.', search: (p: any) => ({ ...p, date: next.toISOString() }) });
  }
}

function MoneyTile({
  label,
  cents,
  count,
  tone,
}: {
  label: string;
  cents?: number;
  count?: number;
  tone: 'neutral' | 'caution' | 'accent';
}) {
  const color =
    tone === 'caution' ? 'var(--state-caution)' : tone === 'accent' ? 'var(--accent-warm)' : 'var(--text-primary)';
  return (
    <div style={S.tile}>
      <span className="label">{label}</span>
      <strong className={cents !== undefined ? 'money' : 'tnum'} style={{ ...S.tileValue, color }}>
        {cents !== undefined ? formatMoney(cents, 'fr') : count}
      </strong>
      {cents !== undefined && count !== undefined && (
        <span style={S.tileNote}>
          {count} {count === 1 ? 'facture' : 'factures'}
        </span>
      )}
    </div>
  );
}

const S = {
  page: { display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 1400 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-4)' },
  title: { fontSize: 'var(--text-2xl)' },
  subtitle: { margin: 'var(--space-1) 0 0', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' },
  dayNav: { display: 'flex', gap: 'var(--space-2)' },
  dayBtn: {
    height: 32, padding: '0 var(--space-3)', background: 'var(--surface-raised)',
    border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)',
    color: 'var(--text-secondary)', font: '500 var(--text-sm)/1 var(--font-body)', cursor: 'pointer',
  },
  moneyRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' },
  tile: {
    display: 'flex', flexDirection: 'column', gap: 2, padding: 'var(--space-4)',
    background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-lg)',
  },
  tileValue: { fontSize: 'var(--text-2xl)', lineHeight: 1.1 },
  tileNote: { fontSize: 'var(--text-xs)', color: 'var(--text-muted)' },
  columns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 'var(--space-4)' },
  panel: {
    background: 'var(--surface-raised)', border: '1px solid var(--border-hairline)',
    borderRadius: 'var(--radius-lg)', overflow: 'hidden',
  },
  panelHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-hairline)',
  },
  panelTitle: { fontSize: 'var(--text-lg)' },
  panelLink: { fontSize: 'var(--text-xs)', color: 'var(--accent)', textDecoration: 'none' },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  item: {
    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-hairline)',
  },
  itemTime: { fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', flex: 'none', width: 44 },
  itemBody: { display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 },
  itemLink: {
    fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)',
    textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  itemMeta: {
    fontSize: 'var(--text-xs)', color: 'var(--text-secondary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  chip: {
    flex: 'none', fontSize: 'var(--text-2xs)', fontWeight: 600, padding: '2px var(--space-2)',
    border: '1px solid', borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap',
  },
  unassigned: { flex: 'none', fontSize: 'var(--text-2xs)', color: 'var(--text-muted)' },
  amountDue: { flex: 'none', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--state-caution)' },
  muted: { margin: 0, padding: 'var(--space-5) var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' },
} satisfies Record<string, CSSProperties>;
